import SafeImage from "components/SafeImage/SafeImage";
import { FC, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { getBlogsById } from "data/api";
import __posts from "data/jsons/__posts.json";
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  NewspaperIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

interface BlogDetail {
  id?: number | string;
  title: string;
  author: string;
  content: string;
  published_date: string;
}

const getPostImage = (id?: string | number) => {
  const index = id ? Number(id) : 0;
  const safeIndex = Number.isNaN(index) ? 0 : index;
  return (__posts as { featuredImage?: string }[])[
    safeIndex % __posts.length
  ]?.featuredImage;
};

const formatDate = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const estimateReadTime = (html: string) => {
  const words = html.replace(/<[^>]*>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const BlogSingle: FC = () => {
  const { id } = useParams();
  const [blogData, setBlogData] = useState<BlogDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getBlogsById(id)
      .then((item) => {
        if (item && item.title) setBlogData(item);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const heroImage = useMemo(() => getPostImage(id), [id]);
  const readTime = useMemo(
    () => (blogData?.content ? estimateReadTime(blogData.content) : 0),
    [blogData?.content]
  );

  if (loading) {
    return (
      <div className="nc-PageSingle min-h-[60vh]">
        <div className="animate-pulse">
          <div className="h-48 sm:h-56 bg-neutral-200 dark:bg-neutral-800" />
          <div className="container px-4 sm:px-6 -mt-20 max-w-4xl mx-auto">
            <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 p-6 sm:p-10 space-y-4">
              <div className="h-4 w-24 rounded bg-neutral-200 dark:bg-neutral-700" />
              <div className="h-8 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
              <div className="h-8 w-3/4 rounded bg-neutral-200 dark:bg-neutral-700" />
              <div className="h-4 w-48 rounded bg-neutral-200 dark:bg-neutral-700" />
            </div>
            <div className="mt-8 h-64 rounded-2xl bg-neutral-200 dark:bg-neutral-700" />
            <div className="mt-8 space-y-3 max-w-3xl mx-auto">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-4 rounded bg-neutral-200 dark:bg-neutral-700"
                  style={{ width: `${85 - i * 5}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blogData) {
    return (
      <div className="container px-4 sm:px-6 py-20 text-center">
        <NewspaperIcon className="w-12 h-12 mx-auto text-neutral-400" />
        <h1 className="mt-4 text-xl font-bold text-neutral-900 dark:text-white">
          Article not found
        </h1>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to blog
        </Link>
      </div>
    );
  }

  return (
    <div className="nc-PageSingle relative overflow-hidden">
      <Helmet>
        <title>{blogData.title} | BCR LTD. UK</title>
        <meta
          name="description"
          content={`${blogData.title} — Expert insights from BCR Ltd on construction profiles, expansion joints, and building solutions across the UAE and GCC.`}
        />
      </Helmet>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1b3d] via-[#152a52] to-[#1a3266]" />
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top_right,rgba(59,130,246,0.35),transparent_55%)]" />
        <div className="container relative px-4 sm:px-6 pt-8 pb-28 sm:pb-32 lg:pb-36">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to all articles
          </Link>

          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white border border-white/20">
              <NewspaperIcon className="w-3.5 h-3.5" />
              BCR Insights
            </span>
            <h1 className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight">
              {blogData.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/75">
              <span className="inline-flex items-center gap-2">
                <UserCircleIcon className="w-4 h-4 text-primary-300" />
                {blogData.author}
              </span>
              <span className="hidden sm:inline text-white/30">|</span>
              <span className="inline-flex items-center gap-2">
                <CalendarDaysIcon className="w-4 h-4 text-primary-300" />
                {formatDate(blogData.published_date)}
              </span>
              {readTime > 0 && (
                <>
                  <span className="hidden sm:inline text-white/30">|</span>
                  <span>{readTime} min read</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Article body */}
      <div className="container relative px-4 sm:px-6 -mt-20 sm:-mt-24 pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto">
          {/* Featured image */}
          {heroImage && (
            <div className="rounded-2xl overflow-hidden border-4 border-white dark:border-neutral-900 shadow-bcr-lg aspect-[21/9] sm:aspect-[2/1]">
              <SafeImage
                src={heroImage}
                alt={blogData.title}
                objectFit="cover"
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content card */}
          <article className="mt-8 sm:mt-10 rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 shadow-bcr-card overflow-hidden">
            <div
              className="bcr-blog-content px-5 sm:px-8 lg:px-12 py-8 sm:py-10 lg:py-12 prose prose-neutral dark:prose-invert prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-[#0f1b3d] dark:prose-headings:text-white prose-headings:tracking-tight
                prose-h2:text-xl sm:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg sm:prose-h3:text-xl
                prose-p:text-neutral-600 dark:prose-p:text-neutral-300 prose-p:leading-relaxed prose-p:text-justify
                prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-neutral-800 dark:prose-strong:text-neutral-100
                prose-img:rounded-xl prose-img:shadow-md prose-img:my-8
                prose-ul:my-4 prose-ol:my-4
                prose-li:text-neutral-600 dark:prose-li:text-neutral-300
                prose-blockquote:border-l-primary-500 prose-blockquote:bg-neutral-50 dark:prose-blockquote:bg-neutral-800/50 prose-blockquote:rounded-r-xl prose-blockquote:py-1 prose-blockquote:not-italic"
              dangerouslySetInnerHTML={{ __html: blogData.content }}
            />
          </article>

          {/* Author card */}
          <div className="mt-8 sm:mt-10 rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-neutral-50 dark:bg-neutral-800/50 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#0f1b3d] flex items-center justify-center">
              <UserCircleIcon className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                Written by
              </p>
              <p className="mt-1 text-lg font-bold text-neutral-900 dark:text-white">
                {blogData.author}
              </p>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                BCR LTD. — Construction profiles & building solutions
              </p>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl text-sm font-semibold bg-[#0f1b3d] hover:bg-[#0a1628] text-white transition-colors shrink-0"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              More articles
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogSingle;
